import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Avatar, Grid, Badge, Chip, Skeleton, Alert, Button,
    useTheme, useMediaQuery, IconButton, CircularProgress
} from '@mui/material';
import {
    DollarSign, Calendar, Users, Percent, Check, Star,
    TrendingUp, RefreshCw, AlertCircle, Shield, AlertTriangle,
    Share2, ArrowRight, ChevronDown, ChevronUp, Sparkles,
    Moon, Sun, Menu
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';

// Import the CircleDataContext hook
import { useCircleData } from './CircleDataContext';
// Import the NextGenSavingsLadder component
import NextGenSavingsLadder from './NextGenSavingsLadder';
// Import the mobile view component
import EnhancedProfileMobile from './EnhancedProfileMobile';
// Import service modules
import { paymentService } from '../../services/paymentService';

/**
 * EnhancedProfileOverview Component - 2025 Edition
 * 
 * A self-contained component that displays user profile data with
 * immersive 3D pricing tier visualization and theme switching.
 */
const EnhancedProfileOverview = ({ 
    user,
    darkMode = false,
    onThemeChange,
    formatShortDate = (date) => {
        if (!date) return '';
        const date_obj = new Date(date);
        if (isNaN(date_obj.getTime())) return '';
        return date_obj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
}) => {
    // State
    const [activeHover, setActiveHover] = useState(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [progressValue, setProgressValue] = useState(0);
    const [isDarkMode, setIsDarkMode] = useState(darkMode);
    const [activeCardIndex, setActiveCardIndex] = useState(0);
    
    // Ref for horizontal scroll container
    const statsScrollRef = useRef(null);
    
    // Get circle data from context
    const { 
        tierData, 
        tiers, 
        isLoading: isCircleDataLoading, 
        error: circleDataError 
    } = useCircleData();
    
    // Auth0 hooks
    const { getAccessTokenSilently } = useAuth0();
    
    // UI state
    const [paymentData, setPaymentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Track dark mode changes
    useEffect(() => {
        setIsDarkMode(darkMode);
    }, [darkMode]);

    // Handle horizontal scroll events
    useEffect(() => {
        const handleScroll = () => {
            if (!statsScrollRef.current || !isMobile) return;
            
            const { scrollLeft, scrollWidth, clientWidth } = statsScrollRef.current;
            const scrollPosition = scrollLeft / (scrollWidth - clientWidth);
            
            // Determine which card is active based on scroll position
            if (scrollPosition < 0.33) {
                setActiveCardIndex(0);
            } else if (scrollPosition < 0.66) {
                setActiveCardIndex(1);
            } else {
                setActiveCardIndex(2);
            }
        };
        
        if (statsScrollRef.current) {
            statsScrollRef.current.addEventListener('scroll', handleScroll);
        }
        
        return () => {
            if (statsScrollRef.current) {
                statsScrollRef.current.removeEventListener('scroll', handleScroll);
            }
        };
    }, [isMobile]);

    // Simulate progress animation with smoother increments
    useEffect(() => {
        // Use RAF (requestAnimationFrame) for smoother animation
        let rafId;
        let startTime;
        const animationDuration = 8000; // 8 seconds for a full cycle
        
        const animate = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const elapsedTime = timestamp - startTime;
            
            // Calculate progress with cubic-bezier easing for natural movement
            // This creates a subtle acceleration and deceleration
            const cycleProgress = (elapsedTime % animationDuration) / animationDuration;
            
            // Apply custom easing - slow start, faster middle, slow end
            let easedProgress;
            
            // Custom sinusoidal easing that feels very natural
            if (cycleProgress <= 0.5) {
                // First half - move from 0 to 50% with easing
                easedProgress = (1 - Math.cos(cycleProgress * 2 * Math.PI)) / 4 * 50;
            } else {
                // Second half - stay at 50% briefly then reset to 0 smoothly
                const resetPhase = (cycleProgress - 0.5) * 2; // 0 to 1
                if (resetPhase < 0.1) {
                    // Hold at 50% briefly
                    easedProgress = 50;
                } else {
                    // Smooth transition back to 0
                    const normalizedPhase = (resetPhase - 0.1) / 0.9; // 0 to 1 
                    easedProgress = 50 * (1 - normalizedPhase);
                }
            }
            
            setProgressValue(easedProgress);
            rafId = requestAnimationFrame(animate);
        };
        
        rafId = requestAnimationFrame(animate);
        
        // Cleanup function
        return () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
        };
    }, []);
    
    // Handle theme toggle
    const toggleTheme = () => {
        const newMode = !isDarkMode;
        setIsDarkMode(newMode);
        if (onThemeChange) {
            onThemeChange(newMode);
        }
    };
    
    // Mouse movement handler for 3D effect
    const handleMouseMove = (e) => {
        const bounds = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - bounds.left;
        const y = e.clientY - bounds.top;
        setMousePosition({ x, y });
    };

    // Scroll to a specific card
    const scrollToCard = (index) => {
        if (!statsScrollRef.current) return;
        
        const cardWidth = statsScrollRef.current.scrollWidth / 3;
        statsScrollRef.current.scrollTo({
            left: cardWidth * index,
            behavior: 'smooth'
        });
    };
    
    // Function to fetch payment data with Auth0 token
    const fetchPaymentData = async () => {
        try {
            setLoading(true);
            setRefreshing(true);
            setError(null);
            
            // Get Auth0 token for API calls
            let token = null;
            try {
                token = await getAccessTokenSilently({
                    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                    scope: 'openid profile email'
                });
            } catch (tokenError) {
                console.error('Error getting Auth0 token:', tokenError);
            }
            
            // Use the paymentService instead of direct axios call
            console.log('Fetching payment overview data...');
            const response = await paymentService.getPaymentOverview(token);
            
            // Process data exactly as Overview.js expects it
            const data = response.data;
            console.log(`Payment API response:`, data);
            
            setPaymentData({
                nextPayment: data.nextPayment,
                activeSubscriptionsCount: data.activeSubscriptions || data.activeSubscriptionsCount || 0,
                paymentHistoryData: data.paymentHistoryData || data.paymentHistory || [],
                spendingByCategoryData: data.spendingByCategoryData || [],
                pendingPayment: data.pendingPayment || null,
                pendingPayments: data.pendingPayments || [],
                verificationStatus: data.verificationStatus || { verified: false, verification_failed: false }
            });
            
        } catch (error) {
            console.error('Error fetching payment data:', error);
            setError('Could not load payment data. Please try again later.');
            
            setPaymentData(null);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    // Fetch payment data on component mount
    useEffect(() => {
        fetchPaymentData();
    }, []);
    
    // Formatting helper functions
    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null) return '0.00';
        return typeof amount === 'number'
            ? amount.toFixed(2)
            : parseFloat(amount || 0).toFixed(2);
    };
    
    const formatPaymentAmount = () => {
        if (loading || !paymentData || !paymentData.nextPayment) return "--";
        const amount = paymentData.nextPayment.amount;
        if (amount === undefined || amount === null) return "--";
        const numAmount = typeof amount === 'number' ? amount : parseFloat(amount || '0');
        if (isNaN(numAmount) || numAmount === 0) return "--";
        return `$${numAmount.toFixed(2)}`;
    };
    
    const formatDueDate = () => {
        if (loading || !paymentData || !paymentData.nextPayment) return "--";
        const date = paymentData.nextPayment.date;
        if (!date) return "--";
        try {
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) return "--";
            return dateObj.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric' 
            });
        } catch (e) {
            return "--";
        }
    };
    
    const formatActiveSubscriptions = () => {
        if (loading || !paymentData) return "--";
        const count = paymentData.activeSubscriptionsCount;
        if (count === undefined || count === null) return "--";
        return count;
    };

    const getActiveSubscriptionsMessage = () => {
        if (loading || !paymentData) return "";
        const count = paymentData.activeSubscriptionsCount;
        if (count === undefined || count === null || count === 0) return "No active subscriptions";
        return `Saving on ${count} plan${count !== 1 ? 's' : ''}`;
    };

    const formatTotalSavings = () => {
        if (!user) return "--";
        const savings = user?.totalSavings;
        if (savings === undefined || savings === null) return "--";
        return `$${parseInt(savings)}`;
    };

    const isUserVerified = () => {
        if (loading || !paymentData || !paymentData.verificationStatus) return false;
        return paymentData.verificationStatus.verified === true;
    };

    const hasVerificationFailed = () => {
        if (loading || !paymentData || !paymentData.verificationStatus) return false;
        return paymentData.verificationStatus.verification_failed === true;
    };

    // Generate a fallback avatar URL with initials
    const getFallbackAvatarUrl = () => {
        const name = user?.name || 'User';
        return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`;
    };
    
    // Handle sharing a circle
    const handleShareCircle = () => {
        console.log("Sharing circle to unlock pricing tier");
        alert("Share functionality would open a modal to invite friends");
    };

    // Pricing Optimization Section - use shared data instead of hardcoded values
    const renderPricingOptimization = () => {
        // If we're loading circle data or payment data
        if (isCircleDataLoading || loading) {
            return (
                <Skeleton 
                    variant="rectangular" 
                    width="100%" 
                    height={180} 
                    animation="wave" 
                    sx={{ 
                        borderRadius: { xs: '12px', sm: '16px' },
                        background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'
                    }}
                />
            );
        }
        
        // If we have an error loading circle data
        if (circleDataError && !tierData) {
            return (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Unable to load pricing data. Please try again later.
                </Alert>
            );
        }
        
        // If we don't have valid tier data
        if (!tierData || !tierData.currentTier) {
            return (
                <Alert severity="info" sx={{ mb: 2 }}>
                    No pricing tiers available at this time.
                </Alert>
            );
        }
        
        // Calculate if we've reached max tier
        const isMaxTier = !tierData.nextTier || (tierData.membersToNextTier <= 0);
        
        // Get the current and next tier prices
        const currentPrice = tierData.currentTier.price_per_line;
        const nextPrice = tierData.nextTier ? tierData.nextTier.price_per_line : currentPrice;
        
        // Calculate the savings
        const savingsPerLine = tierData.nextTier 
            ? currentPrice - nextPrice 
            : 0;
            
        // Get the member counts
        const currentMembers = tierData.currentTier.line_count;
        const nextMembers = tierData.nextTier ? tierData.nextTier.line_count : currentMembers;
        const membersNeeded = tierData.membersToNextTier > 0 ? tierData.membersToNextTier : 0;
        
        return (
            <Box 
                sx={{ 
                    mt: 1,
                    position: 'relative',
                    minHeight: { xs: 160, sm: 180 },
                    borderRadius: { xs: '12px', sm: '16px' },
                    overflow: 'visible'
                }}
                onMouseMove={handleMouseMove}
            >
                <Box className="tier-3d-container">
                    <Box 
                        className="tier-3d-scene"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            perspective: '1000px',
                            width: '100%'
                        }}
                    >
                        {/* Current tier */}
                        <motion.div 
                            className="tier-3d-card current"
                            animate={{
                                rotateX: mousePosition.y > 0 ? -5 : 0,
                                rotateY: mousePosition.x > 0 ? 5 : -5,
                            }}
                            transition={{ type: "spring", stiffness: 50, damping: 10 }}
                            style={{
                                position: 'relative',
                                width: '40%',
                                height: isMobile ? '140px' : '160px',
                                padding: isMobile ? '1rem' : '1.5rem',
                                borderRadius: isMobile ? '12px' : '16px',
                                boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)',
                                background: isDarkMode 
                                    ? 'linear-gradient(135deg, #1e293b, #334155)' 
                                    : 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                                border: isDarkMode 
                                    ? '1px solid rgba(255, 255, 255, 0.1)' 
                                    : '1px solid rgba(255, 255, 255, 0.8)',
                                transformStyle: 'preserve-3d',
                                transform: 'translateZ(20px)',
                                overflow: 'hidden'
                            }}
                        >
                            <Box 
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    zIndex: 2
                                }}
                            >
                                <Typography 
                                    sx={{
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        marginBottom: '0.5rem',
                                        color: isDarkMode 
                                            ? 'rgba(255, 255, 255, 0.7)' 
                                            : 'rgba(15, 23, 42, 0.7)'
                                    }}
                                >
                                    Current
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', marginBottom: '0.5rem' }}>
                                    <Typography 
                                        sx={{
                                            fontSize: { xs: '0.875rem', sm: '1rem' },
                                            fontWeight: 600,
                                            marginRight: '0.125rem',
                                            color: isDarkMode ? '#f1f5f9' : '#0f172a'
                                        }}
                                    >
                                        $
                                    </Typography>
                                    <Typography 
                                        sx={{
                                            fontSize: { xs: '1.75rem', sm: '2.25rem' },
                                            fontWeight: 700,
                                            lineHeight: 1,
                                            color: isDarkMode ? '#f1f5f9' : '#0f172a'
                                        }}
                                    >
                                        {formatCurrency(currentPrice)}
                                    </Typography>
                                    <Typography 
                                        sx={{
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                            marginLeft: '0.25rem',
                                            color: isDarkMode 
                                                ? 'rgba(255, 255, 255, 0.6)' 
                                                : 'rgba(15, 23, 42, 0.6)'
                                        }}
                                    >
                                        /mo
                                    </Typography>
                                </Box>
                                <Typography 
                                    sx={{
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        color: isDarkMode 
                                            ? 'rgba(255, 255, 255, 0.7)' 
                                            : 'rgba(15, 23, 42, 0.7)'
                                    }}
                                >
                                    {currentMembers} Members
                                </Typography>
                            </Box>
                            <Box 
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: isDarkMode
                                        ? 'linear-gradient(to bottom, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0))'
                                        : 'linear-gradient(to bottom, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0))',
                                    transform: 'translateZ(0px)',
                                    opacity: 0.3
                                }}
                            />
                        </motion.div>
                        
                        {/* Arrow */}
                        <Box 
                            sx={{
                                width: '15%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: isDarkMode ? '#38bdf8' : '#0284c7',
                                transform: 'translateZ(10px)'
                            }}
                        >
                            {!isMaxTier && (
                                <motion.div
                                    animate={{ x: [0, 10, 0] }}
                                    transition={{ repeat: Infinity, duration: 2 }}
                                >
                                    <ArrowRight size={isMobile ? 20 : 24} />
                                </motion.div>
                            )}
                            {isMaxTier && (
                                <motion.div
                                    animate={{ scale: [1, 1.1, 1] }}
                                    transition={{ repeat: Infinity, duration: 2 }}
                                >
                                    <Check size={isMobile ? 20 : 24} />
                                </motion.div>
                            )}
                        </Box>
                        
                        {/* Next tier - or Max Tier if reached */}
                        <motion.div 
                            className="tier-3d-card next"
                            animate={{
                                rotateX: mousePosition.y > 0 ? -5 : 0,
                                rotateY: mousePosition.x > 0 ? 5 : -5,
                                y: !isMaxTier ? [0, -5, 0] : 0
                            }}
                            transition={{ 
                                rotateX: { type: "spring", stiffness: 50, damping: 10 },
                                rotateY: { type: "spring", stiffness: 50, damping: 10 },
                                y: { repeat: Infinity, duration: 4 }
                            }}
                            style={{
                                position: 'relative',
                                width: '40%',
                                height: isMobile ? '140px' : '160px',
                                padding: isMobile ? '1rem' : '1.5rem',
                                borderRadius: isMobile ? '12px' : '16px',
                                boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)',
                                background: isDarkMode
                                    ? 'linear-gradient(135deg, #155e75, #0c4a6e)'
                                    : 'linear-gradient(135deg, #e0f2fe, #bae6fd)',
                                border: isDarkMode
                                    ? '1px solid rgba(56, 189, 248, 0.3)'
                                    : '1px solid rgba(56, 189, 248, 0.5)',
                                transformStyle: 'preserve-3d',
                                transform: 'translateZ(20px)',
                                overflow: 'visible'
                            }}
                        >
                            <Box 
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: isDarkMode
                                        ? 'radial-gradient(circle at 50% 50%, rgba(56, 189, 248, 0.3) 0%, rgba(56, 189, 248, 0) 70%)'
                                        : 'radial-gradient(circle at 50% 50%, rgba(14, 165, 233, 0.2) 0%, rgba(14, 165, 233, 0) 70%)',
                                    opacity: 0.5,
                                    animation: 'pulse 3s ease-in-out infinite'
                                }}
                            />
                            <Box 
                                sx={{
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    zIndex: 2
                                }}
                            >
                                {!isMaxTier ? (
                                    <>
                                        <Typography 
                                            sx={{
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                                marginBottom: '0.5rem',
                                                color: isDarkMode
                                                    ? 'rgba(186, 230, 253, 0.9)'
                                                    : 'rgba(12, 74, 110, 0.9)'
                                            }}
                                        >
                                            Next Tier
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', marginBottom: '0.5rem' }}>
                                            <Typography 
                                                sx={{
                                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                                    fontWeight: 600,
                                                    marginRight: '0.125rem',
                                                    color: isDarkMode ? '#38bdf8' : '#0284c7'
                                                }}
                                            >
                                                $
                                            </Typography>
                                            <Typography 
                                                sx={{
                                                    fontSize: { xs: '1.75rem', sm: '2.25rem' },
                                                    fontWeight: 700,
                                                    lineHeight: 1,
                                                    color: isDarkMode ? '#38bdf8' : '#0284c7'
                                                }}
                                            >
                                                {formatCurrency(nextPrice)}
                                            </Typography>
                                            <Typography 
                                                sx={{
                                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                    marginLeft: '0.25rem',
                                                    color: isDarkMode
                                                        ? 'rgba(186, 230, 253, 0.8)'
                                                        : 'rgba(12, 74, 110, 0.8)'
                                                }}
                                            >
                                                /mo
                                            </Typography>
                                        </Box>
                                        <Typography 
                                            sx={{
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                color: isDarkMode
                                                    ? 'rgba(186, 230, 253, 0.8)'
                                                    : 'rgba(12, 74, 110, 0.8)'
                                            }}
                                        >
                                            {nextMembers} Members
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <Typography 
                                            sx={{
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                                marginBottom: '0.5rem',
                                                color: isDarkMode
                                                    ? 'rgba(186, 230, 253, 0.9)'
                                                    : 'rgba(12, 74, 110, 0.9)'
                                            }}
                                        >
                                            MAX TIER
                                        </Typography>
                                        <motion.div
                                            animate={{ 
                                                scale: [1, 1.05, 1],
                                            }}
                                            transition={{ 
                                                repeat: Infinity,
                                                duration: 3,
                                                ease: 'easeInOut'
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: { xs: 50, sm: 60 },
                                                    height: { xs: 50, sm: 60 },
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '50%',
                                                    background: isDarkMode
                                                        ? 'rgba(56, 189, 248, 0.2)'
                                                        : 'rgba(186, 230, 253, 0.5)',
                                                    mb: 1
                                                }}
                                            >
                                                <Check size={isMobile ? 24 : 32} color={isDarkMode ? '#38bdf8' : '#0284c7'} />
                                            </Box>
                                        </motion.div>
                                        <Typography 
                                            sx={{
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                fontWeight: 500,
                                                color: isDarkMode
                                                    ? 'rgba(186, 230, 253, 0.8)'
                                                    : 'rgba(12, 74, 110, 0.8)'
                                            }}
                                        >
                                            Maximum discount!
                                        </Typography>
                                    </>
                                )}
                            </Box>
                            <Box 
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: isDarkMode
                                        ? 'linear-gradient(to bottom, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0))'
                                        : 'linear-gradient(to bottom, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0))',
                                    transform: 'translateZ(0px)',
                                    opacity: 0.3
                                }}
                            />
                            
                            {/* Savings Badge */}
                            {!isMaxTier && savingsPerLine > 0 && (
                                <motion.div 
                                    style={{
                                        position: 'absolute',
                                        bottom: isMobile ? '-20px' : '-25px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        background: isDarkMode
                                            ? 'linear-gradient(90deg, #06b6d4, #0ea5e9)'
                                            : 'linear-gradient(90deg, #0284c7, #0369a1)',
                                        color: 'white',
                                        fontSize: isMobile ? '0.7rem' : '0.75rem',
                                        fontWeight: 600,
                                        padding: isMobile ? '0.4rem 0.65rem' : '0.5rem 0.75rem',
                                        borderRadius: '20px',
                                        whiteSpace: 'nowrap',
                                        boxShadow: isDarkMode
                                            ? '0 0 15px rgba(56, 189, 248, 0.5)'
                                            : '0 0 15px rgba(14, 165, 233, 0.3)',
                                        zIndex: 10
                                    }}
                                    animate={{ scale: [1, 1.05, 1] }}
                                    transition={{ repeat: Infinity, duration: 2 }}
                                >
                                    Save ${formatCurrency(savingsPerLine)}/mo
                                </motion.div>
                            )}
                            
                            {/* Particles */}
                            <motion.div
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    overflow: 'hidden',
                                    borderRadius: isMobile ? '12px' : '16px'
                                }}
                                animate={{ opacity: [0.4, 0.8, 0.4] }}
                                transition={{ repeat: Infinity, duration: 3 }}
                            >
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <Box
                                        key={`particle-${i}`}
                                        sx={{
                                            position: 'absolute',
                                            width: '5px',
                                            height: '5px',
                                            borderRadius: '50%',
                                            background: isDarkMode ? '#38bdf8' : '#0284c7',
                                            opacity: 0.6,
                                            top: `${20 + i * 15}%`,
                                            left: `${10 + i * 15}%`,
                                            animation: `floatParticle ${4 + i}s linear infinite ${i * 0.5}s`
                                        }}
                                    />
                                ))}
                            </motion.div>
                        </motion.div>
                    </Box>
                </Box>
            </Box>
        );
    };

    // Progress section with member needed message
    const renderProgressSection = () => {
        // Skip if loading or no data
        if (isCircleDataLoading || !tierData) return null;
        
        // Determine if we're at max tier
        const isMaxTier = !tierData.nextTier || (tierData.membersToNextTier <= 0);
        
        // If at max tier, don't show this section
        if (isMaxTier) return null;
        
        // Get the values we need
        const currentPrice = tierData.currentTier.price_per_line;
        const nextPrice = tierData.nextTier ? tierData.nextTier.price_per_line : currentPrice;
        const membersNeeded = tierData.membersToNextTier > 0 ? tierData.membersToNextTier : 0;
        
        return (
            <Box sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 0.5, sm: 1 } }}>
                {/* Enhanced header with extra context */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, sm: 2 } }}>
                    <Box 
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            fontWeight: 500,
                            color: isDarkMode ? '#e2e8f0' : '#334155'
                        }}
                    >
                        <Users size={isMobile ? 14 : 16} style={{ color: isDarkMode ? '#38bdf8' : '#0284c7' }} />
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                Need {membersNeeded} more member{membersNeeded !== 1 ? 's' : ''}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: { xs: '0.675rem', sm: '0.75rem' } }}>
                                To unlock next tier savings
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Progress bar */}
                <Box sx={{ mb: 1 }}>
                    <motion.div 
                        className="relative h-2 w-full rounded-md overflow-hidden"
                        sx={{
                            height: isMobile ? '6px' : '8px',
                            background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                        }}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.5 }}
                    >
                        <motion.div 
                            className="absolute top-0 left-0 h-full rounded-md"
                            sx={{
                                width: `${progressValue}%`,
                                background: isDarkMode 
                                    ? 'linear-gradient(90deg, #06b6d4, #0ea5e9)'
                                    : 'linear-gradient(90deg, #0ea5e9, #0284c7)',
                            }}
                            transition={{ 
                                duration: 0.2, 
                                ease: "easeInOut" 
                            }}
                        />
                        <motion.div 
                            className="absolute top-1/2 rounded-full"
                            style={{ 
                                left: `${progressValue}%`,
                                width: isMobile ? '12px' : '18px',
                                height: isMobile ? '12px' : '18px',
                                background: isDarkMode ? 'rgba(56, 189, 248, 0.9)' : 'rgba(2, 132, 199, 0.9)',
                                boxShadow: isDarkMode
                                    ? '0 0 10px rgba(56, 189, 248, 0.8)'
                                    : '0 0 10px rgba(2, 132, 199, 0.5)',
                                border: '2px solid rgba(255, 255, 255, 0.8)',
                                transform: 'translate(-50%, -50%)',
                                display: progressValue > 0 ? 'block' : 'none'
                            }}
                            animate={{ 
                                scale: [1, 1.1, 1],
                            }}
                            transition={{ 
                                repeat: Infinity,
                                duration: 2,
                                ease: "easeInOut"
                            }}
                        />
                    </motion.div>
                </Box>
            </Box>
        );
    };

    // Check if there are any pending payments to show urgent alerts
    const hasPendingPayments = () => {
        if (loading || !paymentData) return false;
        return (paymentData.pendingPayments && paymentData.pendingPayments.length > 0) || 
               (paymentData.pendingPayment && paymentData.pendingPayment.amount > 0);
    };

    // If mobile view, render the mobile-specific component
    if (isMobile) {
        return (
            <EnhancedProfileMobile
                user={user}
                isDarkMode={isDarkMode}
                toggleTheme={toggleTheme}
                statsScrollRef={statsScrollRef}
                activeCardIndex={activeCardIndex}
                progressValue={progressValue}
                scrollToCard={scrollToCard}
                paymentData={paymentData}
                loading={loading}
                error={error}
                refreshing={refreshing}
                fetchPaymentData={fetchPaymentData}
                formatPaymentAmount={formatPaymentAmount}
                formatDueDate={formatDueDate}
                formatActiveSubscriptions={formatActiveSubscriptions}
                getActiveSubscriptionsMessage={getActiveSubscriptionsMessage}
                formatTotalSavings={formatTotalSavings}
                formatShortDate={formatShortDate}
                isUserVerified={isUserVerified}
                getFallbackAvatarUrl={getFallbackAvatarUrl}
                handleShareCircle={handleShareCircle}
                tierData={tierData}
                tiers={tiers}
                isCircleDataLoading={isCircleDataLoading}
                circleDataError={circleDataError}
            />
        );
    }

    // Desktop view
    return (
        <Box 
            className={`myprofile-welcome-section myprofile-fade-in ${isDarkMode ? 'dark-mode' : 'light-mode'}`} 
            mb={{ xs: 0, sm: 4, md: 6 }}
            sx={{
                color: isDarkMode ? '#f1f5f9' : '#0f172a',
                transition: 'all 0.3s ease',
                width: { xs: '100%', sm: 'auto' }, // Full width on mobile
                borderRadius: { xs: 0, sm: '24px' }, // No border radius on mobile
                margin: { xs: 0 }, // No margins on mobile
                padding: { xs: 0 } // No padding on mobile
            }}
        >
            {/* Custom styles for animation keyframes */}
            <style jsx="true">{`
                @keyframes shine {
                    0% { left: -100%; }
                    100% { left: 200%; }
                }
                
                @keyframes float1 {
                    0%, 100% { transform: translate(0, 0); }
                    50% { transform: translate(-5%, 5%); }
                }
                
                @keyframes float2 {
                    0%, 100% { transform: translate(0, 0); }
                    50% { transform: translate(5%, -5%); }
                }
                
                @keyframes float3 {
                    0%, 100% { transform: translate(0, 0); }
                    50% { transform: translate(-8%, -8%); }
                }
                
                @keyframes pulse {
                    0%, 100% { opacity: 0.3; }
                    50% { opacity: 0.6; }
                }
                
                @keyframes floatParticle {
                    0% { transform: translate(0, 0); }
                    25% { transform: translate(10px, 10px); }
                    50% { transform: translate(0, 20px); }
                    75% { transform: translate(-10px, 10px); }
                    100% { transform: translate(0, 0); }
                }
                
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                .spin-animation {
                    animation: spin 1.5s linear infinite;
                }
            `}</style>
            
            {/* Desktop only theme toggle */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                mb: { xs: 1, sm: 2 },
                display: { xs: 'none', sm: 'flex' }
            }}>
                <IconButton 
                    onClick={toggleTheme}
                    sx={{
                        bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                        color: isDarkMode ? '#f1f5f9' : '#0f172a',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'
                        }
                    }}
                >
                    {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                </IconButton>
            </Box>
            
            {/* Error alert - only shown when there's an error */}
            {error && (
                <Box mb={2} sx={{ px: { xs: 2, sm: 0 } }}>
                    <Alert 
                        severity="warning" 
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={fetchPaymentData}
                                disabled={refreshing}
                                sx={{ minWidth: 'auto', p: 0.5 }}
                            >
                                <RefreshCw size={16} className={refreshing ? "spin-animation" : ""} />
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                </Box>
            )}
            
            {/* Main profile card */}
            <Box 
                className="card-content" 
                sx={{ 
                    position: 'relative', 
                    zIndex: 1, 
                    mt: { xs: 3, sm: 0 }
                }}
            >
                <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}> {/* Reduced grid spacing on mobile */}
                    
                    {/* ACTIVITY STATUS - MOVED TO TOP FOR URGENT ACTIONS */}
                    <Grid item xs={12}>
                        <Box>
                            <Typography 
                                variant="subtitle2" 
                                fontWeight={600} 
                                mb={1}
                                sx={{ 
                                    color: isDarkMode ? '#f1f5f9' : '#0f172a',
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <AlertCircle 
                                    size={isMobile ? 14 : 16} 
                                    style={{ 
                                        color: hasPendingPayments() ? '#F44336' : (isDarkMode ? '#38bdf8' : '#0284c7')
                                    }} 
                                />
                                Activity Status
                                {hasPendingPayments() && (
                                    <Chip
                                        label="Action Required"
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        sx={{
                                            height: { xs: 20, sm: 24 },
                                            fontSize: { xs: '0.625rem', sm: '0.75rem' },
                                            fontWeight: 600,
                                            animation: 'pulse 2s ease-in-out infinite'
                                        }}
                                    />
                                )}
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 2 }, mt: 1 }}>
                                {/* Payment Pending Status - show all pending/past due invoices with loading state */}
                                {loading ? (
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        p: { xs: 1, sm: 1.5 }, 
                                        borderRadius: { xs: 1.5, sm: 2 },
                                        bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                                        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)'
                                    }}>
                                        <Box 
                                            sx={{ 
                                                mr: 2, 
                                                bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)', 
                                                borderRadius: '50%',
                                                width: { xs: 32, sm: 36 },
                                                height: { xs: 32, sm: 36 },
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Skeleton variant="circular" width={isMobile ? 16 : 18} height={isMobile ? 16 : 18} />
                                        </Box>
                                        <Box flexGrow={1}>
                                            <Skeleton width={120} height={24} />
                                            <Skeleton width={160} height={18} />
                                        </Box>
                                        <Skeleton width={80} height={36} sx={{ ml: 1 }} />
                                    </Box>
                                ) : (paymentData?.pendingPayments && paymentData.pendingPayments.length > 0) ? (
                                    // Map through all pending payments
                                    paymentData.pendingPayments.map((payment, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                        >
                                            <Box 
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    p: { xs: 1.5, sm: 2 }, 
                                                    borderRadius: { xs: 1.5, sm: 2 },
                                                    bgcolor: payment.status === 'past_due' 
                                                        ? 'rgba(255, 152, 0, 0.1)' 
                                                        : 'rgba(244, 67, 54, 0.1)',
                                                    border: payment.status === 'past_due'
                                                        ? '1px solid rgba(255, 152, 0, 0.2)'
                                                        : '1px solid rgba(244, 67, 54, 0.2)',
                                                    boxShadow: payment.status === 'past_due'
                                                        ? '0 4px 12px rgba(255, 152, 0, 0.15)'
                                                        : '0 4px 12px rgba(244, 67, 54, 0.15)',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        transition: 'transform 0.2s ease'
                                                    }
                                                }}
                                            >
                                                <Box 
                                                    sx={{ 
                                                        mr: { xs: 1.5, sm: 2 }, 
                                                        bgcolor: payment.status === 'past_due'
                                                            ? 'rgba(255, 152, 0, 0.2)'
                                                            : 'rgba(244, 67, 54, 0.2)', 
                                                        borderRadius: '50%',
                                                        width: { xs: 32, sm: 36 },
                                                        height: { xs: 32, sm: 36 },
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <AlertCircle size={isMobile ? 16 : 18} color={payment.status === 'past_due' ? "#FF9800" : "#F44336"} />
                                                </Box>
                                                <Box sx={{ flexGrow: 1 }}>
                                                    <Typography 
                                                        variant="body2" 
                                                        fontWeight={600}
                                                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                                    >
                                                        {payment.status === 'past_due' 
                                                            ? 'Payment Past Due' 
                                                            : payment.status === 'failed'
                                                            ? 'Payment Failed'
                                                            : 'Payment Pending'}
                                                    </Typography>
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                                        }}
                                                    >
                                                        {payment.status === 'past_due' 
                                                            ? 'You have an overdue payment of ' 
                                                            : payment.status === 'failed'
                                                            ? 'Your payment of '
                                                            : 'You have a payment due for '}
                                                        ${payment.amount.toFixed(2)}
                                                        {payment.number && ` (Invoice: ${payment.number})`}
                                                        {payment.dueDate && ` due on ${formatShortDate(payment.dueDate)}`}
                                                    </Typography>
                                                </Box>
                                                {payment.invoiceUrl && (
                                                    <Button 
                                                        size="small" 
                                                        variant="contained" 
                                                        color={payment.status === 'past_due' ? "warning" : "error"}
                                                        href={payment.invoiceUrl} 
                                                        target="_blank"
                                                        sx={{ 
                                                            ml: 1,
                                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                            py: { xs: 0.5, sm: 0.75 },
                                                            px: { xs: 1, sm: 1.5 },
                                                            fontWeight: 600,
                                                            textTransform: 'none',
                                                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                                                            '&:hover': {
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: '0 6px 12px rgba(0, 0, 0, 0.25)'
                                                            }
                                                        }}
                                                    >
                                                        Pay Now
                                                    </Button>
                                                )}
                                            </Box>
                                        </motion.div>
                                    ))
                                ) : (paymentData?.pendingPayment) ? (
                                    // Fallback to original single pendingPayment for backward compatibility
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                    >
                                        <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            p: { xs: 1.5, sm: 2 }, 
                                            borderRadius: { xs: 1.5, sm: 2 },
                                            bgcolor: 'rgba(244, 67, 54, 0.1)',
                                            border: '1px solid rgba(244, 67, 54, 0.2)',
                                            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                transition: 'transform 0.2s ease'
                                            }
                                        }}>
                                            <Box 
                                                sx={{ 
                                                    mr: { xs: 1.5, sm: 2 }, 
                                                    bgcolor: 'rgba(244, 67, 54, 0.2)', 
                                                    borderRadius: '50%',
                                                    width: { xs: 32, sm: 36 },
                                                    height: { xs: 32, sm: 36 },
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <AlertCircle size={isMobile ? 16 : 18} color="#F44336" />
                                            </Box>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography 
                                                    variant="body2" 
                                                    fontWeight={600}
                                                    sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                                >
                                                    Payment Pending
                                                </Typography>
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                                        fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                                    }}
                                                >
                                                    You have a payment due for ${paymentData.pendingPayment.amount.toFixed(2)}
                                                </Typography>
                                            </Box>
                                            {paymentData.pendingPayment.invoiceUrl && (
                                                <Button 
                                                    size="small" 
                                                    variant="contained" 
                                                    color="error"
                                                    href={paymentData.pendingPayment.invoiceUrl} 
                                                    target="_blank"
                                                    sx={{ 
                                                        ml: 1,
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        py: { xs: 0.5, sm: 0.75 },
                                                        px: { xs: 1, sm: 1.5 },
                                                        fontWeight: 600,
                                                        textTransform: 'none',
                                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                                                        '&:hover': {
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.25)'
                                                        }
                                                    }}
                                                >
                                                    Pay Now
                                                </Button>
                                            )}
                                        </Box>
                                    </motion.div>
                                ) : (
                                    // No pending payments - show all clear status
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        p: { xs: 1.5, sm: 2 }, 
                                        borderRadius: { xs: 1.5, sm: 2 },
                                        bgcolor: 'rgba(76, 175, 80, 0.1)',
                                        border: '1px solid rgba(76, 175, 80, 0.2)'
                                    }}>
                                        <Box 
                                            sx={{ 
                                                mr: { xs: 1.5, sm: 2 }, 
                                                bgcolor: 'rgba(76, 175, 80, 0.2)', 
                                                borderRadius: '50%',
                                                width: { xs: 32, sm: 36 },
                                                height: { xs: 32, sm: 36 },
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Check size={isMobile ? 16 : 18} color="#4CAF50" />
                                        </Box>
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography 
                                                variant="body2" 
                                                fontWeight={600}
                                                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                            >
                                                All Clear
                                            </Typography>
                                            <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                                }}
                                            >
                                                No pending payments or actions required
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Grid>

                    {/* User Profile Section */}
                    <Grid item xs={12} md={12} mb={{ xs: 1, sm: 2 }}>
                        {/* Desktop Profile Layout */}
                        <Box 
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: { xs: 'center', md: 'flex-start' },
                                pt: { xs: 1, sm: 0 }
                            }}
                        >
                            {/* Welcome text with integrated avatar - Full width and centered on mobile */}
                            <Box 
                                flexGrow={1} 
                                sx={{ 
                                    width: '100%',
                                    textAlign: { xs: 'center', md: 'left' }
                                }}
                            >
                                <Box 
                                    display="flex" 
                                    alignItems="center" 
                                    justifyContent={{ xs: 'center', md: 'flex-start' }} 
                                    mb={{ xs: 2, sm: 3 }}
                                    gap={2}
                                >
                                    {/* Avatar integrated with welcome text */}
                                    <Badge
                                        overlap="circular"
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        badgeContent={
                                            isUserVerified() ? (
                                                <Box
                                                    className="verification-badge"
                                                    sx={{
                                                        bgcolor: '#4CAF50',
                                                        color: 'white',
                                                        width: { xs: 16, sm: 20 },
                                                        height: { xs: 16, sm: 20 },
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        border: '2px solid white'
                                                    }}
                                                >
                                                    <Check size={10} />
                                                </Box>
                                            ) : null
                                        }
                                    >
                                        <Avatar
                                            src={getFallbackAvatarUrl()}
                                            sx={{
                                                width: { xs: 48, sm: 56 },
                                                height: { xs: 48, sm: 56 },
                                                border: '3px solid',
                                                borderColor: isUserVerified() ? '#4CAF50' : 'primary.main',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                    </Badge>
                                    
                                    <Typography 
                                        variant="h5" 
                                        fontWeight={700} 
                                        sx={{ 
                                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                            background: 'linear-gradient(90deg, #FF4775, #FF7676)',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            backgroundClip: 'text'
                                        }}
                                    >
                                        Welcome back, {user?.name?.split(' ')[0] || 'User'}!
                                    </Typography>
                                </Box>

                                {/* Stats grid */}
                                <Grid container spacing={2}>
                                    {/* Next Payment */}
                                    <Grid item xs={12} sm={4}>
                                        <Box 
                                            className="welcome-stat-box"
                                            sx={{
                                                background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                                                borderRadius: { xs: '16px', sm: '16px' },
                                                padding: { xs: '16px', sm: '16px' },
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    background: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                                                    transform: 'translateY(-2px)'
                                                }
                                            }}
                                        >
                                            <Box display="flex" alignItems="center">
                                                <Box 
                                                    sx={{
                                                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                                        width: { xs: 42, sm: 40 },
                                                        height: { xs: 42, sm: 40 },
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#4CAF50'
                                                    }}
                                                >
                                                    <DollarSign size={isMobile ? 18 : 20} />
                                                </Box>
                                                <Box ml={1.5}>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                        }}
                                                    >
                                                        Next Payment
                                                    </Typography>
                                                    {loading ? (
                                                        <Skeleton width={80} height={32} animation="wave" />
                                                    ) : (
                                                        <Typography 
                                                            variant="h6" 
                                                            fontWeight={600}
                                                            sx={{ 
                                                                fontSize: { xs: '1.25rem', sm: '1.25rem' },
                                                                color: isDarkMode ? '#f1f5f9' : '#0f172a' 
                                                            }}
                                                        >
                                                            {formatPaymentAmount()}
                                                        </Typography>
                                                    )}
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                                            fontSize: { xs: '0.675rem', sm: '0.75rem' }
                                                        }}
                                                    >
                                                        Due {formatDueDate()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    {/* Active Circles */}
                                    <Grid item xs={12} sm={4}>
                                        <Box 
                                            className="welcome-stat-box"
                                            sx={{
                                                background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                                                borderRadius: { xs: '16px', sm: '16px' },
                                                padding: { xs: '16px', sm: '16px' },
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    background: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                                                    transform: 'translateY(-2px)'
                                                }
                                            }}
                                        >
                                            <Box display="flex" alignItems="center">
                                                <Box 
                                                    sx={{
                                                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                                        width: { xs: 42, sm: 40 },
                                                        height: { xs: 42, sm: 40 },
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#2196F3'
                                                    }}
                                                >
                                                    <Users size={isMobile ? 18 : 20} />
                                                </Box>
                                                <Box ml={1.5}>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                        }}
                                                    >
                                                        Active Circles
                                                    </Typography>
                                                    {loading ? (
                                                        <Skeleton width={80} height={32} animation="wave" />
                                                    ) : (
                                                        <Typography 
                                                            variant="h6" 
                                                            fontWeight={600}
                                                            sx={{ 
                                                                fontSize: { xs: '1.25rem', sm: '1.25rem' },
                                                                color: isDarkMode ? '#f1f5f9' : '#0f172a' 
                                                            }}
                                                        >
                                                            {formatActiveSubscriptions()}
                                                        </Typography>
                                                    )}
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                                            fontSize: { xs: '0.675rem', sm: '0.75rem' }
                                                        }}
                                                    >
                                                        {getActiveSubscriptionsMessage()}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    {/* Total Savings */}
                                    <Grid item xs={12} sm={4}>
                                        <Box 
                                            className="welcome-stat-box"
                                            sx={{
                                                background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                                                borderRadius: { xs: '16px', sm: '16px' },
                                                padding: { xs: '16px', sm: '16px' },
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    background: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                                                    transform: 'translateY(-2px)'
                                                }
                                            }}
                                        >
                                            <Box display="flex" alignItems="center">
                                                <Box 
                                                    sx={{
                                                        backgroundColor: 'rgba(255, 193, 7, 0.1)',
                                                        width: { xs: 42, sm: 40 },
                                                        height: { xs: 42, sm: 40 },
                                                        borderRadius: '50%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#FFC107'
                                                    }}
                                                >
                                                    <Percent size={isMobile ? 18 : 20} />
                                                </Box>
                                                <Box ml={1.5}>
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                                        }}
                                                    >
                                                        Total Savings
                                                    </Typography>
                                                    <Typography 
                                                        variant="h6" 
                                                        fontWeight={600}
                                                        sx={{ 
                                                            fontSize: { xs: '1.25rem', sm: '1.25rem' },
                                                            color: isDarkMode ? '#f1f5f9' : '#0f172a' 
                                                        }}
                                                    >
                                                        {formatTotalSavings()}
                                                    </Typography>
                                                    <Typography 
                                                        variant="caption" 
                                                        sx={{ 
                                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                                                            fontSize: { xs: '0.675rem', sm: '0.75rem' }
                                                        }}
                                                    >
                                                        Since {formatShortDate(user?.joinDate)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    </Grid>

                    {/* PRICING VISUALIZATION - Updated to use shared data */}
                    <Grid item xs={12} md={7}>
                        <Box>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography 
                                    variant="subtitle2" 
                                    fontWeight={600}
                                    sx={{ 
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: isDarkMode ? '#f1f5f9' : '#0f172a',
                                        fontSize: { xs: '0.875rem', sm: '1rem' }
                                    }}
                                >
                                    <Sparkles 
                                        size={isMobile ? 14 : 16} 
                                        style={{ 
                                            marginRight: '8px',
                                            color: isDarkMode ? '#c084fc' : '#8b5cf6'
                                        }} 
                                    />
                                    Pricing Optimization
                                </Typography>
                                <Chip
                                    label="Savings Unlocked"
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        borderColor: isDarkMode ? 'rgba(99, 102, 241, 0.5)' : 'rgba(79, 70, 229, 0.5)',
                                        color: isDarkMode ? '#6366f1' : '#4f46e5',
                                        height: { xs: 24, sm: 28 },
                                        fontSize: { xs: '0.675rem', sm: '0.75rem' }
                                    }}
                                />
                            </Box>

                            {renderPricingOptimization()}
                            {renderProgressSection()}
                        </Box>
                    </Grid>

                    {/* PRICING TIERS - NextGenSavingsLadder that uses CircleDataContext */}
                    <Grid item xs={12} md={5}>
                        <NextGenSavingsLadder 
                            darkMode={isDarkMode}
                            handleShareCircle={handleShareCircle}
                        />
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default EnhancedProfileOverview;